// frontend/packages/business-console/src/components/OfflineStatusChip.jsx
//
// Work item 2.19 — top-bar status chip for the quote / invoice
// editors. Spec source: `docs/design/quotes-invoices-console.md` §7a.
//
// States:
//   ● Online — Saved Ns ago         (default, neutral)
//   ● Offline — Saved locally Ns ago (amber)
//   ● Syncing… N of M               (transient, info-blue)
//   ● Sync failed — 1 needs attention (red)

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

function formatAgo(ts) {
  if (!ts) return '';
  const secs = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(ts).toLocaleString();
}

export default function OfflineStatusChip({
  online,
  isFlushing,
  pendingCount,
  failedCount,
  lastSavedAt,
}) {
  if (failedCount > 0) {
    return (
      <Tooltip
        title={`${failedCount} draft${failedCount === 1 ? '' : 's'} need${
          failedCount === 1 ? 's' : ''
        } attention. Visit Drafts to fix.`}
      >
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineOutlinedIcon fontSize="small" />}
          label={`Sync failed — ${failedCount} need${
            failedCount === 1 ? 's' : ''
          } attention`}
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  if (isFlushing) {
    return (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        icon={<SyncOutlinedIcon fontSize="small" />}
        label={
          pendingCount > 0 ? `Syncing… ${pendingCount} pending` : 'Syncing…'
        }
        sx={{ fontWeight: 600 }}
      />
    );
  }

  if (!online) {
    return (
      <Tooltip
        title={`You're offline. ${
          pendingCount > 0
            ? `${pendingCount} draft${
                pendingCount === 1 ? '' : 's'
              } saved locally; will sync on reconnect.`
            : 'Edits will save locally and sync on reconnect.'
        }`}
      >
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<CloudOffOutlinedIcon fontSize="small" />}
          label={
            lastSavedAt
              ? `Offline — Saved locally ${formatAgo(lastSavedAt)}`
              : 'Offline'
          }
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<CloudDoneOutlinedIcon fontSize="small" />}
      label={
        lastSavedAt ? `Online — Saved ${formatAgo(lastSavedAt)}` : 'Online'
      }
      sx={{ fontWeight: 600, color: 'text.secondary' }}
    />
  );
}
